import { computed, observable, isObservableArray, toJS } from 'mobx';
import get from 'lodash/get';
import { compareValuesArray } from '../utils/compareValues';
import { performanceCheck as checkPerformance } from '../utils/performanceCheck';
import { InclusionClauses } from '../models/config';
import { ShareClass, ShareClassDocument } from '../models/entity';
import { Stores } from '../models/Stores';
import { BaseStore, ConfigStore } from '.';

export class DocumentStore extends BaseStore {
    @observable config: ConfigStore;
    @observable shareClass: ShareClass;
    @observable.ref globalDocuments: Array<ShareClassDocument>;

    constructor(
        stores: Stores,
        config: ConfigStore,
        shareClass: ShareClass,
        globalDocuments: Array<ShareClassDocument>
    ) {
        super(stores);
        this.config = config;
        this.shareClass = shareClass;
        this.globalDocuments = globalDocuments;
    }

    // Add global documents to shareclass documents
    @computed
    get shareClassDocuments(): Array<ShareClassDocument> {
        const { documents = [] } = this.shareClass;
        const allDocuments = documents.concat(this.globalDocuments);

        // Return unique documents
        return Array.from(new Set(allDocuments));
    }

    // Filter documents with documentSegmentationFilters
    @computed
    get filteredDocuments(): Array<ShareClassDocument> {
        const {
            inclusionClauses = [],
        } = this.config.documentSegmentationFilters;

        const { data } = this.stores;
        const documentFilters = toJS(data.documentFilterSearch);

        if (inclusionClauses.length > 0) {
            let includedDocuments = [];

            // Aggregate included documents
            for (const inclusionClause of inclusionClauses) {
                const { clauses, performanceCheck } = inclusionClause;

                const hasPerformance = checkPerformance({
                    dataStore: this.stores.data,
                    config: this.config,
                    shareClass: this.shareClass,
                });

                // Filter documents by each set of clauses
                includedDocuments = includedDocuments.concat(
                    this.shareClassDocuments.filter(d =>
                        this.documentsFilter(
                            d,
                            clauses,
                            performanceCheck,
                            hasPerformance
                        )
                    )
                );
            }

            // Return unique documents
            return Array.from(new Set(includedDocuments));
        }

        if (documentFilters.length > 0) {
            let includedDocuments = [];

            for (const filter of documentFilters) {
                // Filter documents by each set of filter
                includedDocuments = includedDocuments.concat(
                    this.shareClassDocuments.filter(d =>
                        this.documentsMatchFilter(d, filter)
                    )
                );
            }

            // Return unique documents
            return Array.from(new Set(includedDocuments));
        }

        return this.shareClassDocuments;
    }

    documentsFilter = (
        document: ShareClassDocument,
        clauses: InclusionClauses,
        performanceCheck: boolean,
        hasPerformance: boolean
    ): boolean => {
        const clauseResults = [];

        // For each clause, record whether the document matches clause critera
        for (const clause of clauses) {
            const {
                property,
                metatype,
                inferredType,
                operator,
                value,
            } = clause as any; // as InclusionClauses;
            const clauseValuesArray = isObservableArray(value)
                ? value
                : [value];

            if (property) {
                const propertyValue = get(
                    this.shareClass,
                    `properties_pub.${property}.value`
                );
                const propertyValuesArray = isObservableArray(propertyValue)
                    ? propertyValue
                    : [propertyValue];
                const isMatch = compareValuesArray(
                    clauseValuesArray,
                    operator,
                    propertyValuesArray
                );
                clauseResults.push(isMatch);
            }

            if (metatype) {
                const metaValue = get(document, `meta.${metatype}.value`);
                const metaValuesArray = isObservableArray(metaValue)
                    ? metaValue
                    : [metaValue];
                const isMatch = compareValuesArray(
                    clauseValuesArray,
                    operator,
                    metaValuesArray
                );
                clauseResults.push(isMatch);
            }

            if (inferredType) {
                const inferredValue = get(document, `inferred.${inferredType}`);
                const valuesArray = isObservableArray(inferredValue)
                    ? inferredValue
                    : [inferredValue];
                const isMatch = compareValuesArray(
                    clauseValuesArray,
                    operator,
                    valuesArray
                );
                clauseResults.push(isMatch);
            }

            if (performanceCheck) {
                clauseResults.push(hasPerformance);
            }
        }

        // Return true only if every clause result is true
        return clauseResults.every(v => v === true);
    };

    documentsMatchFilter(document: ShareClassDocument, filter) {
        const values = filter.values;
        const clauseResults = [];

        const metaValue = get(document, `meta.${filter.property}.value`);
        const metaValuesArray = isObservableArray(metaValue)
            ? metaValue
            : [metaValue];
        const isMatch = compareValuesArray(values, '=', metaValuesArray);
        clauseResults.push(isMatch);

        return clauseResults.every(v => v === true);
    }
}



// WEBPACK FOOTER //
// ./src/stores/DocumentStore.ts